import { computed, DestroyRef, inject, Injectable, linkedSignal, signal } from '@angular/core';
import { PomodoroTimer, PomodoroTimerDTO } from '../models/pomodoro-timer.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, first, map, Observable, of, retry, timer } from 'rxjs';
import { HttpClient, HttpContext } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { ConfirmService } from '@atsystems/ui';
import { playBeep } from '../helpers/utils';
import { SKIP_ERROR_INTERCEPTOR } from '../helpers/errors.interceptor';

@Injectable({
  providedIn: 'root',
})
export class PomodoroTimerService {
  private http = inject(HttpClient);
  public data = signal<PomodoroTimer | null>(null);
  public destroyRef = inject(DestroyRef);
  public confirmService = inject(ConfirmService);

  private retryConfig = {
    count: 3,
    delay: (error: any, retryCount: number) => {
      return timer(1000 * retryCount);
    }
  };

  public getData(): Observable<PomodoroTimer> {
    return this.http.get<PomodoroTimerDTO>(`${environment.bff_service}/pomodoro-timer/get`, {
      context: new HttpContext().set(SKIP_ERROR_INTERCEPTOR, true)
    }).pipe(
      retry(this.retryConfig),
      map(e => e?.config),
      catchError(() => of({} as PomodoroTimer))
    )
  }

  public setTimerConfig(config: PomodoroTimer): Observable<void> {
    return this.http.post<void>(`${environment.bff_service}/pomodoro-timer/set`, config).pipe(takeUntilDestroyed(this.destroyRef));
  }

  public channel = new BroadcastChannel('pomodoro-timer');

  public isActive = linkedSignal(() => !!this.data()?.openByDefault);
  public isPaused = linkedSignal(() => !this.data()?.lastStart);
  public startTime = linkedSignal(() => {
    const lastStart = this.data()?.lastStart;
    return lastStart ? new Date(lastStart) : null;
  });
  public workDuration = computed(() => this.data()?.workDuration ?? 25);
  public remainingTime = signal<number>(this.workDuration() * 60 * 1000);

  public currentIteration = linkedSignal(() => this.data()?.iteration.current ?? 1);
  public totalIterations = linkedSignal(() => this.data()?.iteration.total ?? 4);
  public shortBreakDuration = linkedSignal(() => this.data()?.shortBreakDuration ?? 5);
  public longBreakDuration = linkedSignal(() => this.data()?.longBreakDuration ?? 15);
  public needConfirm = linkedSignal(() => this.data()?.needConfirm ?? true);
  public needSound = linkedSignal(() => this.data()?.needSound ?? true);

  public currentDuration = linkedSignal(() => {
    if (this.currentIteration() === this.totalIterations()) {
      return this.longBreakDuration();
    } else if (this.currentIteration() % 2 === 0) {
      return this.shortBreakDuration();
    } else {
      return this.workDuration();
    }
  });
  public currentDurationType = computed(() => {
    if (this.currentIteration() === this.totalIterations()) {
      return 'Long-break';
    } else if (this.currentIteration() % 2 === 0) {
      return 'Short-break';
    } else {
      return 'Work';
    }
  });

  public endTime = computed(() => {
    const start = this.startTime();
    return start ? new Date(start.getTime() + this.currentDuration() * 60 * 1000) : null;
  });

  private intervalId?: ReturnType<typeof setInterval>;

  public sendEventToBroadcastChannel(event: any) {
    this.channel.postMessage(event);
  }

  constructor() {
    this.channel.onmessage = event => {
      if (event.data.reload) {
        this.stopInterval();
        this.refreshData();
      }
    };

    this.refreshData();
  }

  public refreshData() {
    this.getData().pipe(
      first()
    ).subscribe(data => {
      this.data.set(data);
      data?.lastStart && this.startTimer();
    });
  }

  private saveData(sendEvent: boolean = true) {
    const payload: PomodoroTimer = {
      lastStart: this.startTime() ?? null,
      workDuration: this.workDuration(),
      shortBreakDuration: this.shortBreakDuration(),
      longBreakDuration: this.longBreakDuration(),
      iteration: {
        current: this.currentIteration(),
        total: this.totalIterations(),
      },
      openByDefault: this.isActive(),
      needConfirm: this.needConfirm(),
      needSound: this.needSound(),
    };

    this.setTimerConfig(payload)
      .pipe(first())
      .subscribe(() => {
        sendEvent && this.sendEventToBroadcastChannel({reload: true});
      });
  }

  public toggleTimer() {
    this.isActive.set(!this.isActive());
    this.saveData();
  }

  public togglePause(autoToggle: boolean = false) {
    if (this.isPaused()) {
      this.startTimer();
    } else {
      this.pauseTimer();
      this.currentIteration.update(prev => (prev === this.totalIterations() ? 1 : prev + 1));
      const durationType = this.currentDurationType();
      if (durationType === 'Work') {
        this.currentDuration.set(this.workDuration());
      } else if (durationType === 'Short-break') {
        this.currentDuration.set(this.shortBreakDuration());
      } else if (durationType === 'Long-break') {
        this.currentDuration.set(this.longBreakDuration());
      }
    }
    this.saveData(!autoToggle);
  }

  public startTimer() {
    this.isPaused.set(false);
    if (!this.startTime()) {
      this.startTime.set(new Date());
    }
    this.startInterval();
  }

  public pauseTimer() {
    this.isPaused.set(true);
    this.startTime.set(null);
    this.stopInterval();
    this.remainingTime.set(this.currentDuration() * 60 * 1000);
  }

  private startInterval() {
    this.stopInterval();
    this.intervalId = setInterval(() => {
      const end = this.endTime();
      const remaining = end ? Math.max(0, end.getTime() - new Date().getTime()) : 0;
      this.remainingTime.set(remaining);

      if (remaining <= 0) {
        this.togglePause(true);
        this.needSound() && playBeep();
        this.needConfirm() &&
        this.confirmService.confirm({
          title: 'Pomodoro timer',
          message: 'Pomodoro timer is finished. Do you want to continue?',
          acceptLabel: 'Continue',
          accept: () => {
            this.togglePause();
          },
          reject: () => { }
        });
      }
    }, 100);
  }

  private stopInterval() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = undefined;
    }
  }
}
